import { Formik, Form } from 'formik'
import React, { FunctionComponent, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import { useMutation } from '@apollo/client/react'
import { Button } from '../../components/Button'
import { Label, Section, SectionColumn, Text } from '../../components/layout'
import { TextInput, CheckboxInput } from '../../components/layout/form/input'
import { setErrorNotifications } from '../../components/responses/setErrorNotifications'
import { ButtonContainer } from '../TowingRecordPage/components/routes/subforms/styled'
import { MultifactorCodeFormValues } from '../user'
import {
  MultifactorAuthenticationCodeResponse,
  MULTIFACTOR_AUTHENTICATION_CODE_MUTATION,
  SendSmsCodeResponse,
  SEND_SMS_VERIFICATION_CODE,
} from '../user/mutation/mutation'
import { toMutationVariables } from '../user/mutation/toMutationVariables'
import { User } from '../../common/types'
import { DispatchStore, isMfaPending, StateStore, userFromToken } from '../StoreProvider'
import styled from 'styled-components'
import {
  getStoredRefreshToken,
  isRunningInWebview,
  storeLogin,
} from '../../graphql-client/storedState'
import { setCurrentUser } from '../StoreProvider/actions'
import { Loading } from '../../components/responses'
import { logout } from '../../util/login'
import * as serviceWorker from '../../serviceWorker'
import { RenewResponse } from '../../components/responses/token/types'
import { RENEW_TOKEN_MUTATION } from '../../components/responses/token/query'
import { setUnauthorizedNotification } from '../../components/notification'
import { StyledSpinner } from '../LoginPage/LoginForm'
import { Redirect } from 'react-router'
import LinkButton from '../../components/LinkButton'
import { formatMessageSafe } from '../../util/translateInputOptions'
import { messages } from './messages'

type SendSMSCodeModalView = 'index' | 'send_code' | 'code_sent' | 'enable_mfa'

const ModalContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100% !important;
  height: 100%;
  overflow: auto;
  background-color: #fff;
  z-index: 1000;
  padding-bottom: 1rem;
  background-color: ${props => props.theme.colors.white};
`
const RouteWrapper = styled.div`
  padding: 0.5rem 1rem;
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    padding: 0.5rem;
  }
`

const ModalWindow = styled.div`
  background-color: ${props => props.theme.colors.white};
  margin: 0 auto;
  border-radius: ${props => props.theme.radius.small};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-top: 8px solid #fabf40;
`

export const parseStatus = (): string => {
  const url = new URL(window.location.href)
  const status = (url.searchParams.get('status') as any) || ''

  return status
}

const MfaPage: FunctionComponent = () => {
  const {
    state: { currentUser },
  } = useContext(StateStore)
  const { dispatch } = useContext(DispatchStore)
  const [view, setView] = useState<SendSMSCodeModalView>('index')
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  // const returnUrl = new URL(window.location.href).searchParams.get('returnUrl') || '/towing-jobs'
  const [submitCode, { loading }] = useMutation<MultifactorAuthenticationCodeResponse>(
    MULTIFACTOR_AUTHENTICATION_CODE_MUTATION,
    {
      async onCompleted(response) {
        const { submitCode } = response
        if (submitCode.__typename === 'MultifactorAuthenticationCodeSuccess') {
          const { accessToken, refreshToken, locationToken } = submitCode
          storeLogin({ accessToken, refreshToken, locationToken })

          const user = userFromToken(accessToken)
          dispatch(setCurrentUser(user))

          if (!isRunningInWebview()) {
            serviceWorker.registerNotificationWorker()
          }

        } else {
          setErrorNotifications({ data: submitCode })
          if (submitCode.error.message === 'Koodi on vanhentunut, ole hyvä ja tilaa uusi koodi.') {
            setView('send_code')
          }
        }
      },
      onError(err) {
        console.error(err)
      },
    }
  )

  const handleLogout = async () => {
    await logout()
  }

  const handleViewChange = (view: SendSMSCodeModalView) => {
    setView(view)
  }

  //avoid endless loading spinner
  if (!currentUser) {
    return <Loading />
  }

  if (!isMfaPending(currentUser)) { 
    return <Redirect to="/towing-jobs" />
  }

  if (
    currentUser.mfa_status === 'pending_tupas_authentication' ||
    currentUser.mfa_status === 'pending_mfa_activation'
  ) {
    return <PendingTupasAuthenticationScreen />
  }

  if (currentUser.mfa_status === 'sms_pending' && view !== 'code_sent' && view !== 'enable_mfa') {
    return <SendSMSCodeModal user={currentUser} view={view} onViewChange={handleViewChange} />
  }

  return (
    <ModalContainer>
      <ModalWindow>
        <RouteWrapper>
          <Formik
            initialValues={{ rememberMe: true, code: '', userId: currentUser.user_id }}
            onSubmit={async (values: MultifactorCodeFormValues) => {
              await submitCode({ variables: toMutationVariables(values) })
            }}
          >
            {() => {
              return (
                  <>
                  <Text style={{ marginBottom: '0.5rem' }}>
                    {formatMessage(messages.loggedInUser)} <Label style={{ fontWeight: 'bold' }}>{currentUser.username}</Label>
                  </Text>

                  <Loading loading={loading} />
                  {(currentUser.mfa_status === 'pending' || view === 'enable_mfa') && (
                    <Form>
                      <Section mobileGap="0.5rem">
                        <SectionColumn flexDirection="column">
                          <TextInput name="code" label={formatMessage(messages.appCodeLabel)} />
                        </SectionColumn>
                        {currentUser && currentUser.mfa_status && Number(currentUser.rememberMeForDays) > 0 && (
                          <SectionColumn>
                            <CheckboxInput
                              name="rememberMe"
                              label={formatMessage(messages.rememberDeviceForDays, { days: currentUser.rememberMeForDays })}
                              margin="0 0 0.1rem 0"
                            />
                          </SectionColumn>
                        )}
                      </Section>
                      <ButtonContainer style={{gap: 6}} floatAtBottomNoMenu>
                        <Button label={formatMessage(messages.cancel)} category="logout" onClick={handleLogout} size="l" maxWidth="100%" />
                        <Button label={formatMessage(messages.send)} category="save" size="l" maxWidth="100%" submit />
                      </ButtonContainer>
                    </Form>
                  )}
                  {currentUser.mfa_status === 'sms_pending' && view === 'code_sent' && (
                    <Form>
                      <Section mobileGap="0.5rem" height="auto" columns={1}>
                        <SectionColumn>
                          <Text>
                            {formatMessage(messages.codeSentPrefix)} <b>{formatMessage(messages.registeredPhoneNumber)}</b>. {formatMessage(messages.codeSentSuffix)}{' '}
                            <b>{formatMessage(messages.smsCodeLabel)}</b> {formatMessage(messages.codeSentFieldSuffix)}
                          </Text>
                        </SectionColumn>
                      </Section>
                      <Section mobileGap="0.5rem" height="auto">
                        <SectionColumn flexDirection="column">
                          <TextInput name="code" label={formatMessage(messages.smsCodeLabel)} maxLength={6} trim />
                        </SectionColumn>
                        {currentUser && currentUser.mfa_status && Number(currentUser.rememberMeForDays) > 0 && (
                          <SectionColumn>
                            <CheckboxInput
                              name="rememberMe"
                              label={formatMessage(messages.rememberDeviceForDays, { days: currentUser.rememberMeForDays })}
                              margin="0 0 0.1rem 0"
                            />
                          </SectionColumn>
                        )}
                      </Section>
                      <ButtonContainer style={{gap: 6}} floatAtBottomNoMenu>
                        <Button label={formatMessage(messages.cancel)} category="logout" onClick={handleLogout} size="l" maxWidth="100%" />
                        <Button label={formatMessage(messages.send)} category="save" size="l" maxWidth="100%" submit />
                      </ButtonContainer>
                    </Form>
                  )}
                </>
              )
            }}
          </Formik>
        </RouteWrapper>
      </ModalWindow>
    </ModalContainer>
  )
}

export default MfaPage

interface MFAModalProps {
  user: User
}
interface SendSMSCodeModalProps extends MFAModalProps {
  onViewChange: (view: SendSMSCodeModalView) => void
  view: SendSMSCodeModalView
}

const SendSMSCodeModal: React.FC<SendSMSCodeModalProps> = ({ view, onViewChange }) => {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const [sendSmsCode, { loading }] = useMutation<SendSmsCodeResponse>(SEND_SMS_VERIFICATION_CODE, {
    onCompleted: async response => {
      const { sendSmsCode } = response
      if (sendSmsCode.__typename === 'SendSmsCodeSuccess') {
        onViewChange('code_sent')
      } else {
        setErrorNotifications({ data: sendSmsCode })
      }
    }
  })

  const handleActivateAuthenticatorClick = () => {
    let url = (process.env.REACT_APP_REST_API_URL as string).replace('rest', 'auth/tupas_auth')

    url += `?returnUrl=${window.location.href}`
    window.open(encodeURI(url))
    onViewChange('enable_mfa')
  }

  const handleUseSmsCodeClick = () => {
    onViewChange('send_code')
  }

  return (
    <ModalContainer>
      <ModalWindow>
        <RouteWrapper>
          <Formik
            initialValues={{ requestCode: true }}
            onSubmit={async () => {
              await sendSmsCode()
            }}
          >
            {({ handleSubmit }) => {
              return (
                <>
                  <Loading loading={loading} />
                  {view === 'send_code' ? (
                    <>
                      <Section mobileGap="0.5rem" height="auto" columns={1}>
                        <SectionColumn>
                          <Text>
                            {formatMessage(messages.sendOneTimeCodePrefix)} <b>{formatMessage(messages.registeredPhoneNumber)}</b>. {formatMessage(messages.sendOneTimeCodeSuffix)}
                          </Text>
                        </SectionColumn>
                      </Section>
                      <ButtonContainer floatAtBottomNoMenu>
                        <Button
                          label={formatMessage(messages.sendCode)}
                          category="save"
                          onClick={handleSubmit}
                          size="l"
                          maxWidth="100%"
                        />
                      </ButtonContainer>
                    </>
                  ) : (
                    <>
                      <Section mobileGap="0.5rem" height="auto" columns={2}>
                        <SectionColumn>
                          <Button
                            label={formatMessage(messages.activateAuthenticator)}
                            category="saveSecondary"
                            onClick={handleActivateAuthenticatorClick}
                            maxWidth="100%"
                            size="m"
                          />
                        </SectionColumn>
                        <SectionColumn>
                          <Button
                            label={formatMessage(messages.sendCodeBySms)}
                            category="new"
                            onClick={handleUseSmsCodeClick}
                            maxWidth="100%"
                            size="m"
                          />
                        </SectionColumn>
                      </Section>
                    </>
                  )}
                </>
              )
            }}
          </Formik>
        </RouteWrapper>
      </ModalWindow>
    </ModalContainer>
  )
}

function PendingTupasAuthenticationScreen() {
  const { dispatch } = useContext(DispatchStore)
  const [showRefreshButton, setShowRefreshButton] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)

  const [renew, { loading }] = useMutation<RenewResponse>(RENEW_TOKEN_MUTATION, {
    onCompleted(response) {
      if (response.renewToken.__typename === 'RenewSuccess') {
        const {
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
          locationToken: newLocationToken,
        } = response.renewToken

        storeLogin({ accessToken: newAccessToken, refreshToken: newRefreshToken, locationToken: newLocationToken })
        const user = userFromToken(newAccessToken)
        dispatch(setCurrentUser(user))

      }
      if (response.renewToken.__typename === 'UnauthorizedError') {
        setUnauthorizedNotification()
      }

      setTimeout(() => {
        setIsRefreshing(false)
      }, 2000)
    },
    onError: () => {
      setUnauthorizedNotification()
      setTimeout(() => {
        setIsRefreshing(false)
      }, 2000)
    },
  })

  const handleTupasAuth = () => {
    const url = process.env.REACT_APP_REST_API_URL as string
    window.open(`${url.replace('rest', 'auth/tupas_auth')}`, '_blank')
    
    setTimeout(() => {
      setShowRefreshButton(true)
    }, 1000)
  }

  const handleRefresh = () => {
    if (isRefreshing) return
    setIsRefreshing(true)
    renew({ variables: { refreshToken: getStoredRefreshToken() } })
  }
  
  const handleLogout = async () => {
    await logout()
  }

  return (
    <ModalContainer>
      <ModalWindow>
        <Loading loading={loading || isRefreshing} />
        <RouteWrapper>
          <Section mobileGap="0.5rem" height="auto" columns={1}>
            <SectionColumn>
              <Text style={{ fontSize: '1.3em', marginBottom: '1rem' }}>
                {formatMessage(messages.tupasRequiredPrefix)} {" "}
                <LinkButton onClick={handleTupasAuth}>{formatMessage(messages.webPortalLink)}</LinkButton>. {formatMessage(messages.tupasRequiredSuffix)}{' '}
              </Text>
            </SectionColumn>
          </Section>
          <ButtonContainer floatAtBottomNoMenu>
            <Button label={formatMessage(messages.cancel)} category="logout" onClick={handleLogout} size="l" maxWidth="100%" />
            <div style={{ flexGrow: 1, marginLeft: '0.25em' }}></div>
            {showRefreshButton ? (
              <>
                <Button
                  label={(loading || isRefreshing) ? '' : formatMessage(messages.refreshView)}
                  category="save"
                  disabled={loading || isRefreshing}
                  onClick={handleRefresh}
                  size="l"
                  maxWidth="100%"
                >
                  {(loading || isRefreshing) && <StyledSpinner />}
                </Button>
              </>
            ) : (
              <Button
                label={formatMessage(messages.twoFactorAuthentication)}
                category="save"
                onClick={handleTupasAuth}
                size="l"
                maxWidth="100%"
              />
            )}
          </ButtonContainer>
        </RouteWrapper>
      </ModalWindow>
    </ModalContainer>
  )
}
